import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api";
import {
  competitionRanks,
  formatCompetitionRank,
  sharedRankFlags,
} from "@/lib/competitionRank";
import { displayName } from "@/lib/displayName";
import { formatBRL, MASKED_BRL } from "@/lib/format";
import {
  computeLeaderboards,
  computePositionDeltas,
  type LeaderboardEntry,
  type LeaderboardSet,
  type Member,
  type PositionDeltas,
  type SettlementSummary,
} from "@/lib/leaderboard";
import { canSeeMoneyValues } from "@/lib/permissions";
import { Card } from "@/components/ui/Card";
import { ScreenSpinner } from "@/components/ui/Spinner";

type Tab = "hits" | "earnings" | "tournaments";

// Resumo do torneio mais recentemente liquidado — vencedores ou, quando
// ninguém ganhou, o pote acumulado + quem mais acertou (hitStats).
type LastResult = {
  tournamentName: string;
  settlement: SettlementSummary;
  nameByUserId: Map<string, string>;
};

const TABS: { key: Tab; label: string }[] = [
  { key: "hits", label: "Acertos" },
  { key: "earnings", label: "Ganhos" },
  { key: "tournaments", label: "Torneios" },
];

const CRITERIA: Record<Tab, string> = {
  hits: "Ranking por número de acertos no Top 3 dos torneios",
  earnings: "Ranking por valor total ganho em prêmios",
  tournaments:
    "Ranking por torneios disputados (mínimo 10 para aparecer na lista)",
};

const EMPTY_MESSAGE: Record<Tab, string> = {
  hits: "Ninguém teve acertos ainda.",
  earnings: "Ninguém ganhou valores ainda.",
  tournaments: "Ninguém participou de 10 ou mais torneios ainda.",
};

function myRankIn(
  list: readonly LeaderboardEntry[],
  userId: string | null,
  tieKey: (e: LeaderboardEntry) => number,
): { position: number; tied: boolean } {
  const idx = userId ? list.findIndex((u) => u.userId === userId) : -1;
  if (idx < 0) return { position: 0, tied: false };
  if (tieKey(list[idx]!) === 0) return { position: 0, tied: false };
  const ranks = competitionRanks(list, tieKey);
  const tied = sharedRankFlags(ranks);
  return { position: ranks[idx]!, tied: tied[idx]! };
}

export default function RankingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("hits");
  const [data, setData] = useState<{
    boards: LeaderboardSet;
    deltas: PositionDeltas;
    lastResult: LastResult | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiFetch<{ members: Member[] }>("/api/members"),
      apiFetch<{ settlements: SettlementSummary[] }>("/api/settlements"),
      apiFetch<{ id: string; name: string }[]>("/api/tournaments", {
        auth: false,
      }),
    ])
      .then(([{ members }, { settlements }, tournaments]) => {
        if (cancelled) return;
        const current = computeLeaderboards(members, settlements);

        // Snapshot anterior: todas as liquidações exceto a mais recente —
        // mostra como cada posição mudou após o último torneio (regra do
        // app original).
        let prevSettlements: SettlementSummary[] = [];
        let lastResult: LastResult | null = null;
        if (settlements.length > 0) {
          const sorted = [...settlements].sort(
            (a, b) =>
              new Date(b.settledAt).getTime() -
              new Date(a.settledAt).getTime(),
          );
          const latest = sorted[0]!;
          prevSettlements = settlements.filter(
            (s) => s.tournamentId !== latest.tournamentId,
          );
          lastResult = {
            tournamentName:
              tournaments.find((t) => t.id === latest.tournamentId)?.name ??
              "Último torneio",
            settlement: latest,
            nameByUserId: new Map(
              members.map((m) => [m.id, displayName(m)]),
            ),
          };
        }
        const previous = computeLeaderboards(members, prevSettlements);
        setData({
          boards: current,
          deltas: computePositionDeltas(current, previous),
          lastResult,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Erro ao carregar rankings.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const uid = user?.id ?? null;
  const showMoney = user ? canSeeMoneyValues(user.role) : false;

  const view = useMemo(() => {
    if (!data) return null;
    const { boards } = data;
    // O ranking "Torneios" oculta quem nunca apostou (regra do original).
    const byTournaments = boards.byTournaments.filter(
      (e) => e.tournamentCount > 0,
    );

    const fullList =
      tab === "hits"
        ? boards.byHits
        : tab === "earnings"
          ? boards.byEarnings
          : byTournaments;

    const tieKey =
      tab === "hits"
        ? (e: LeaderboardEntry) => e.hits
        : tab === "earnings"
          ? (e: LeaderboardEntry) => e.totalWon
          : (e: LeaderboardEntry) => e.tournamentCount;

    const qualifies = (e: LeaderboardEntry): boolean =>
      tab === "hits"
        ? e.hits > 0
        : tab === "earnings"
          ? e.totalWon > 0
          : e.tournamentCount >= 10;

    const ranks = competitionRanks(fullList, tieKey);
    const tied = sharedRankFlags(ranks);

    // Posição do usuário atual na lista da aba (Infinity quando ausente):
    // todo participante ranqueado acima dele fica sempre visível, para ele
    // ver quem precisa superar.
    const currentUserIdx = fullList.findIndex((e) => e.userId === uid);
    const currentUserRank =
      currentUserIdx >= 0 ? ranks[currentUserIdx]! : Infinity;

    const visible: { entry: LeaderboardEntry; rank: number; tied: boolean }[] =
      [];
    fullList.forEach((e, idx) => {
      if (qualifies(e) || e.userId === uid || ranks[idx]! < currentUserRank) {
        visible.push({ entry: e, rank: ranks[idx]!, tied: tied[idx]! });
      }
    });

    const byTournamentsSet: LeaderboardSet = {
      ...boards,
      byTournaments,
    };
    return { visible, boards: byTournamentsSet };
  }, [data, tab, uid]);

  if (error) {
    return (
      <div className="flex flex-col gap-4 px-5 pt-6">
        <h1 className="text-xl font-bold">Rankings</h1>
        <Card className="p-6 text-center text-sm text-[#ffb4ad]">{error}</Card>
      </div>
    );
  }
  if (!data || !view) return <ScreenSpinner />;

  const { boards } = view;
  const hitsRank = myRankIn(boards.byHits, uid, (e) => e.hits);
  const earningsRank = myRankIn(boards.byEarnings, uid, (e) => e.totalWon);
  const tournamentsRank = myRankIn(
    boards.byTournaments,
    uid,
    (e) => e.tournamentCount,
  );

  const deltaMap =
    tab === "hits"
      ? data.deltas.hits
      : tab === "earnings"
        ? data.deltas.earnings
        : data.deltas.tournaments;

  return (
    <div className="flex flex-col px-5 pt-6">
      <p className="text-[11px] font-bold tracking-[2px] text-muted-foreground">
        RANKINGS
      </p>
      <h1 className="mt-1 mb-4 text-[28px] leading-tight font-bold">
        Participantes
      </h1>

      {data.lastResult && (
        <LastResultCard result={data.lastResult} showMoney={showMoney} />
      )}

      {/* Tabs */}
      <div className="flex rounded-(--radius-field) border border-border bg-secondary p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`flex-1 rounded-[9px] py-2.5 text-[13px] font-semibold transition-colors ${
              tab === t.key
                ? "bg-surface-alt text-surface-alt-foreground"
                : "text-muted-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Sua posição — sempre visível, três colunas fixas */}
      <Card className="mt-4 p-3.5">
        <p className="mb-3 text-center text-[10px] font-bold tracking-[1.5px] text-muted-foreground">
          SUA POSIÇÃO
        </p>
        <div className="flex items-center">
          <MyPositionCol label="Acertos" rank={hitsRank} />
          <div className="mx-1 h-9 w-px bg-border" />
          <MyPositionCol label="Ganhos" rank={earningsRank} />
          <div className="mx-1 h-9 w-px bg-border" />
          <MyPositionCol label="Torneios" rank={tournamentsRank} />
        </div>
      </Card>

      <p className="mt-2.5 text-center text-[11px] text-muted-foreground">
        {CRITERIA[tab]}
      </p>

      {/* Lista */}
      <div className="mt-5 flex flex-col gap-2 pb-6">
        {view.visible.length === 0 ? (
          <p className="mt-6 text-center text-sm font-medium text-muted-foreground">
            {EMPTY_MESSAGE[tab]}
          </p>
        ) : (
          view.visible.map(({ entry, rank, tied: isTied }) => (
            <LeaderRow
              key={entry.userId}
              entry={entry}
              position={rank}
              tied={isTied}
              isMe={entry.userId === uid}
              tab={tab}
              showMoney={showMoney}
              delta={deltaMap.get(entry.userId)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Card "Último torneio": vencedores ou, sem vencedores, o aviso de pote
// acumulado + quem mais acertou. Valores individuais de prêmio seguem a
// máscara de dinheiro dos Rankings (só admin vê); o valor do pote acumulado
// é sempre visível (incentivo, mesma regra do Pote de Ganhadores da home).
function LastResultCard({
  result,
  showMoney,
}: {
  result: LastResult;
  showMoney: boolean;
}) {
  const { settlement, tournamentName, nameByUserId } = result;
  const noWinners = settlement.winnersCount === 0;

  // Prêmio por vencedor a partir dos payouts (fallback: prizePerWinner).
  const winners = (
    settlement.winnerPayouts && settlement.winnerPayouts.length > 0
      ? settlement.winnerPayouts.map((p) => ({
          userId: p.userId,
          prize: p.prize,
        }))
      : settlement.winnerUserIds.map((userId) => ({
          userId,
          prize: settlement.prizePerWinner,
        }))
  ).map((w) => ({
    name: nameByUserId.get(w.userId) ?? "Participante",
    prize: w.prize,
  }));

  const topHitters = (settlement.hitStats ?? [])
    .filter((h) => h.hits > 0)
    .sort((a, b) => b.hits - a.hits)
    .slice(0, 5);

  return (
    <Card className="mb-4 p-3.5">
      <p className="text-[10px] font-bold tracking-[1.5px] text-muted-foreground">
        ÚLTIMO TORNEIO
      </p>
      <p className="mt-1 text-sm font-semibold">{tournamentName}</p>
      {noWinners ? (
        <>
          <p className="mt-2 text-[13px] text-muted-foreground">
            Ninguém acertou o Top 3 completo.
          </p>
          {(settlement.jackpotAdded ?? 0) > 0 && (
            <p className="mt-1 text-[13px] font-semibold text-accent">
              💰 Pote de {formatBRL(settlement.jackpotAdded!)} acumulou para o
              próximo torneio!
            </p>
          )}
          {topHitters.length > 0 && (
            <div className="mt-2.5">
              <p className="text-[10px] font-bold tracking-[1.5px] text-muted-foreground">
                QUEM CHEGOU PERTO
              </p>
              <div className="mt-1.5 flex flex-col gap-1">
                {topHitters.map((h) => (
                  <div
                    key={h.userId}
                    className="flex items-center justify-between"
                  >
                    <p className="min-w-0 flex-1 truncate text-[13px] font-medium">
                      {nameByUserId.get(h.userId) ?? "Participante"}
                    </p>
                    <p className="shrink-0 text-xs font-bold text-muted-foreground">
                      {h.hits} de 3
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mt-2 flex flex-col gap-1">
          {winners.map((w, i) => (
            <div key={i} className="flex items-center justify-between">
              <p className="min-w-0 flex-1 truncate text-[13px] font-medium">
                🏆 {w.name}
              </p>
              <p className="shrink-0 text-xs font-bold text-accent">
                {showMoney ? formatBRL(w.prize) : MASKED_BRL}
              </p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function MyPositionCol({
  label,
  rank,
}: {
  label: string;
  rank: { position: number; tied: boolean };
}) {
  return (
    <div className="flex flex-1 flex-col items-center gap-1">
      <p className="text-[11px] font-medium tracking-[0.3px] text-muted-foreground">
        {label}
      </p>
      <p className="text-[22px] font-bold">
        {rank.position > 0 ? (
          <>
            <span className="text-tint">#</span>
            {formatCompetitionRank(rank.position, rank.tied).slice(1)}
          </>
        ) : (
          "—"
        )}
      </p>
    </div>
  );
}

function DeltaBadge({
  delta,
  isMe,
}: {
  delta: number | undefined;
  isMe: boolean;
}) {
  const muted = isMe ? "text-white/50" : "text-muted-foreground";
  if (delta === undefined) {
    return (
      <span
        className={`min-w-8 text-center text-[9px] font-semibold tracking-[0.5px] ${muted}`}
      >
        NEW
      </span>
    );
  }
  if (delta === 0) {
    return (
      <span className={`min-w-8 text-center text-xs font-semibold ${muted}`}>
        —
      </span>
    );
  }
  const up = delta > 0;
  const color = up
    ? isMe
      ? "text-[rgba(134,239,172,0.9)]"
      : "text-[#16a34a]"
    : isMe
      ? "text-[rgba(252,165,165,0.9)]"
      : "text-[#dc2626]";
  return (
    <span className={`min-w-8 text-center text-[11px] font-bold ${color}`}>
      {up ? `▲${delta}` : `▼${Math.abs(delta)}`}
    </span>
  );
}

function LeaderRow({
  entry,
  position,
  tied,
  isMe,
  tab,
  showMoney,
  delta,
}: {
  entry: LeaderboardEntry;
  position: number;
  tied: boolean;
  isMe: boolean;
  tab: Tab;
  showMoney: boolean;
  delta: number | undefined;
}) {
  const isPodium = position <= 3;
  return (
    <div
      className={`flex items-center gap-2 rounded-(--radius-btn) border p-2.5 ${
        isMe
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card"
      }`}
    >
      <span
        className={`flex h-[29px] w-[29px] shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
          isPodium
            ? "bg-accent text-accent-foreground"
            : isMe
              ? "bg-white/15 text-primary-foreground"
              : "bg-secondary text-foreground"
        }`}
      >
        {formatCompetitionRank(position, tied)}
      </span>
      <DeltaBadge delta={delta} isMe={isMe} />
      <p className="min-w-0 flex-1 truncate text-sm font-semibold">
        {displayName(entry)}
      </p>
      <p className="shrink-0 text-sm font-bold">
        {tab === "hits"
          ? `${entry.hits} acertos`
          : tab === "earnings"
            ? showMoney
              ? formatBRL(entry.totalWon)
              : MASKED_BRL
            : `${entry.tournamentCount} torneios`}
      </p>
    </div>
  );
}
